import React from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, ContactRound, LayoutGrid } from "lucide-react";
import { useAuth } from "@context/auth";
import {
  canCreate,
  openCreatePicker,
} from "@components/CreateOptionPickerCst/host";
import { V2Route, V2RouteChild, V2RouteSection } from "./types";

export const useNavigationRoutesV2 = (
  dataDetail: Record<string, any> | undefined,
): V2RouteSection[] => {
  const { t } = useTranslation("sidebar");
  const { user } = useAuth();

  const allSections: V2RouteSection[] = [
    {
      key: "menu",
      label: t("sections.menu") || "",
      type: "cards",
      items: [
        {
          kind: "card",
          key: "my-space",
          icon: React.createElement(LayoutGrid, { size: 18 }),
          title: t("cards.my-space.title") || "",
          description: t("cards.my-space.description") || "",
          to: "my-space",
        },
        {
          kind: "card",
          key: "catalog",
          title: t("cards.training-offer.title") || "",
          description: t("cards.training-offer.description") || "",
          icon: React.createElement(BookOpen, { size: 18 }),
          to: "catalog",
          section: "fe.corporate.catalog",
          createKind: "catalog",
        },
        {
          kind: "card",
          key: "users-groups",
          title: t("cards.users-groups.title") || "",
          description: t("cards.users-groups.description") || "",
          icon: React.createElement(ContactRound, { size: 18 }),
          to: "users-groups",
          section: "corporate.users",
          createKind: "users-groups",
        },
      ],
    },
  ];

  const isItemVisible = (item: V2Route): boolean => {
    if (item.section && !user?.permissions?.includes(item.section)) {
      return false;
    }
    if (item.requiresDataFlag && !dataDetail?.[item.requiresDataFlag]) {
      return false;
    }
    return true;
  };

  const isChildVisible = (child: V2RouteChild): boolean => {
    if (child.section && !user?.permissions?.includes(child.section)) {
      return false;
    }
    return true;
  };

  const withActionButton = (item: V2Route): V2Route => {
    if (!item.createKind || !canCreate(user, item.createKind)) return item;
    const kind = item.createKind;
    return {
      ...item,
      actionButton: {
        label: t("create-button") || "",
        onClick: () => openCreatePicker(kind),
      },
    };
  };

  return allSections
    .map((section) => ({
      ...section,
      items: section.items.filter(isItemVisible).map((item) => ({
        ...withActionButton(item),
        children: item.children?.filter(isChildVisible),
      })),
    }))
    .filter((section) => section.items.length > 0);
};

export * from "./types";
